/** Publishing (M8) — typed API bindings. */
import { http } from "@/shared/api/http";

export type JobStatus = "queued" | "running" | "succeeded" | "failed" | "dead_letter" | "cancelled";

export interface JobEvent {
  type: "enqueued" | "leased" | "started" | "succeeded" | "failed" | "retry_scheduled" | "dead_lettered" | "cancelled" | "manual_retry";
  at: string;
  attempt: number;
  latency_ms: number | null;
  error_kind: string | null;
  error_message: string | null;
  provider_response: Record<string, unknown> | null;
}

export interface RetryStats {
  first_failure_at: string | null;
  last_failure_at: string | null;
  consecutive_failures: number;
  total_failures: number;
  mean_retry_ms: number;
  total_retry_ms: number;
}

export interface PublishingJob {
  id: string;
  workspace_id: string;
  scheduled_asset_id: string;
  asset_id: string;
  permanent_asset_id: string | null;
  campaign_pack_id: string | null;
  channel: string;
  provider_key: string;
  status: JobStatus;
  attempts: number;
  max_attempts: number;
  next_run_at: string | null;
  last_run_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  correlation_id: string | null;
  provider_response: Record<string, unknown> | null;
  last_error_kind: string | null;
  last_error_message: string | null;
  dead_letter_reason: string | null;
  stats: RetryStats;
  event_history: JobEvent[];
  created_at: string;
  updated_at: string;
}

export interface QueueHealth {
  counts: Record<JobStatus, number>;
  total_jobs: number;
  delivered_24h: number;
  succeeded_24h: number;
  dead_letter_24h: number;
  success_rate: number;
  p95_latency_ms: number;
  retry_count_total: number;
  avg_retry_ms: number;
  workers_active: number;
  queue_depth: number;
}

export interface ProviderMeta {
  channel: string;
  key: string;
  kind: string;
}

export const listProviders = (): Promise<ProviderMeta[]> => http(`/publishing/providers`);

export const getQueueHealth = (workspaceId: string): Promise<QueueHealth> =>
  http(`/workspaces/${workspaceId}/publishing/queue/health`);

export const listJobs = (
  workspaceId: string,
  params: { status?: JobStatus; channel?: string; limit?: number } = {},
): Promise<PublishingJob[]> => {
  const p = new URLSearchParams();
  if (params.status) p.set("status", params.status);
  if (params.channel) p.set("channel", params.channel);
  if (params.limit) p.set("limit", String(params.limit));
  const q = p.toString();
  return http(`/workspaces/${workspaceId}/publishing/queue/jobs${q ? `?${q}` : ""}`);
};

export const getJob = (workspaceId: string, jobId: string): Promise<PublishingJob> =>
  http(`/workspaces/${workspaceId}/publishing/queue/jobs/${jobId}`);

export const retryJob = (workspaceId: string, jobId: string): Promise<PublishingJob> =>
  http(`/workspaces/${workspaceId}/publishing/queue/jobs/${jobId}/retry`, { method: "POST" });

export const cancelJob = (workspaceId: string, jobId: string): Promise<PublishingJob> =>
  http(`/workspaces/${workspaceId}/publishing/queue/jobs/${jobId}/cancel`, { method: "POST" });

export const listDeadLetter = (workspaceId: string, limit = 25): Promise<PublishingJob[]> =>
  http(`/workspaces/${workspaceId}/publishing/queue/dead-letter?limit=${limit}`);

export const JOB_STATUS_TONES: Record<JobStatus, string> = {
  queued:      "bg-sky-100 text-sky-900 dark:bg-sky-950/60 dark:text-sky-200",
  running:     "bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200 animate-pulse",
  succeeded:   "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200",
  failed:      "bg-orange-100 text-orange-900 dark:bg-orange-950/60 dark:text-orange-200",
  dead_letter: "bg-rose-100 text-rose-900 dark:bg-rose-950/60 dark:text-rose-200",
  cancelled:   "bg-zinc-100 text-zinc-500 dark:bg-zinc-950/60 dark:text-zinc-400",
};

// ── M10 Phase 3: Publishing credential vault + AI activity ───────
export type ConnectionStatus =
  | "disconnected" | "pending_oauth" | "connected" | "expired" | "error" | "sandbox";

export interface PublishingConnection {
  id: string;
  channel: string;
  display_name: string;
  status: ConnectionStatus;
  account_label: string | null;
  scopes_granted: string[];
  token_expires_at: string | null;
  last_tested_at: string | null;
  last_test_result: string | null;
  last_error: string | null;
  connected_at: string | null;
  profile: {
    authorize_url: string | null;
    token_url: string | null;
    scopes: string[];
    docs_url: string | null;
    supports_refresh: boolean;
    supports_test_connection: boolean;
    scope_description: string;
  } | null;
}

export const listConnections = (ws: string): Promise<PublishingConnection[]> =>
  http(`/workspaces/${ws}/publishing/connections`);

export const startOAuth = (
  ws: string, body: { channel: string; redirect_uri: string },
): Promise<{ channel: string; authorize_url: string; scopes: string[]; state: string; note: string }> =>
  http(`/workspaces/${ws}/publishing/connections/oauth/start`, { method: "POST", body });

export const sandboxConnect = (
  ws: string, body: { channel: string; account_label: string },
): Promise<PublishingConnection> =>
  http(`/workspaces/${ws}/publishing/connections/sandbox`, { method: "POST", body });

export const testConnection = (
  ws: string, channel: string,
): Promise<{ channel: string; status: ConnectionStatus; result: string }> =>
  http(`/workspaces/${ws}/publishing/connections/${channel}/test`, { method: "POST" });

export const disconnect = (ws: string, channel: string): Promise<PublishingConnection> =>
  http(`/workspaces/${ws}/publishing/connections/${channel}`, { method: "DELETE" });

export interface AiActivity {
  requests_last_hour: number;
  tokens_last_hour: number;
  cost_usd_last_hour: number;
  avg_latency_ms: number;
  providers: Array<{ provider: string; model: string; requests: number; cost_usd: number }>;
  recent: Array<{
    provider: string; model: string; content_type: string;
    cost_usd: number; latency_ms: number; timestamp: string | null; status: string;
  }>;
  budget_status: string;
}

export const getAiActivity = (ws: string): Promise<AiActivity> =>
  http(`/workspaces/${ws}/ai/activity`);
